import { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'wouter';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ArrowLeft, MapPin, Home, Phone, Clock, ShoppingCart, Package, Loader2, AlertCircle, CreditCard, Info, Gift, Check, UtensilsCrossed } from 'lucide-react';
import { useInitiateCheckout, CheckoutResponse, CheckoutCartItem } from '@/hooks/useCheckout';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import KudozDisplay from '@/components/KudozDisplay';
import { useUserProfile, SavedAddress, SavedPhone } from '@/hooks/useUserProfile';
import { format } from 'date-fns';
import EmbeddedCheckout from '@/components/EmbeddedCheckout';
import { useOrderDelays } from '@/hooks/useOrderableMenu';
import { useAsapQuoteRefresh } from '@/utils/asapQuote';
import { AuthenticatedMap } from '@/components/AuthenticatedMap';

interface ModifierOption {
  id: string;
  name: string;
  price: number;
  available?: boolean;
  isDefault?: boolean;
  nestedModifiers?: ModifierGroup[];
}

interface ModifierGroup {
  id: string;
  name: string;
  required: boolean;
  multiSelect: boolean;
  numFree?: number;
  options: ModifierOption[];
}

interface CartMenuItem {
  id: string;
  name: string;
  price: string | number | null;
  description?: string | null;
  imageUrl?: string | null;
  modifiers?: ModifierGroup[];
}

interface OrderConfig {
  serviceType: 'delivery' | 'takeout';
  timing: 'asap' | 'scheduled';
  scheduledTime: string | null;
  scheduledStart: number | null;
  scheduledEnd: number | null;
  addressId: number | null;
  // Address coordinates for map display (fallback when address lookup fails)
  addressLatitude: number | null;
  addressLongitude: number | null;
}

function getOrderConfigFromStorage(): OrderConfig | null {
  try {
    // First check the cart data for embedded orderConfig (primary source)
    const savedCartData = localStorage.getItem('eatzy-cart');
    if (savedCartData) {
      const cartData = JSON.parse(savedCartData);
      if (cartData.orderConfig) {
        console.log('[Checkout] Using orderConfig from cart:', cartData.orderConfig);
        // Preserve scheduledStart/scheduledEnd for Eatzy API
        const config = cartData.orderConfig;
        return {
          serviceType: config.serviceType || 'delivery',
          timing: config.timing || 'asap',
          scheduledTime: config.scheduledTime || (config.scheduledStart ? new Date(config.scheduledStart * (config.scheduledStart > 1e12 ? 1 : 1000)).toISOString() : null),
          scheduledStart: config.scheduledStart || null,
          scheduledEnd: config.scheduledEnd || null,
          addressId: config.addressId || null,
          // Include address coordinates for map fallback
          addressLatitude: config.addressLatitude || null,
          addressLongitude: config.addressLongitude || null,
        };
      }
    }
    
    // Fallback to separate order config key (legacy)
    const savedConfig = localStorage.getItem('eatzy-order-config');
    if (savedConfig) {
      console.log('[Checkout] Using orderConfig from separate key');
      const parsed = JSON.parse(savedConfig);
      return {
        ...parsed,
        addressLatitude: parsed.addressLatitude || null,
        addressLongitude: parsed.addressLongitude || null,
      };
    }
  } catch (e) {
    console.error('Failed to parse order config:', e);
  }
  return null;
}

function formatScheduledTime(scheduledTime: string | null, timing: string, timezone?: string): string {
  if (timing === 'asap') {
    return 'ASAP (25-35 min)';
  }
  if (scheduledTime) {
    try {
      const date = new Date(scheduledTime);
      const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

      const todayInTz = new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
      const dateInTz = new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
      const isToday = todayInTz === dateInTz;

      const dateStr = isToday
        ? 'Today'
        : new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short', month: 'short', day: 'numeric' }).format(date);
      const timeStr = new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit', hour12: true }).format(date);
      return `${dateStr}, ${timeStr}`;
    } catch {
      return scheduledTime;
    }
  }
  return 'ASAP (25-35 min)';
}

interface CartItem {
  item: CartMenuItem;
  quantity: number;
  selections: { [groupId: string]: string[] };
  specialInstructions?: string;
}

interface CartData {
  restaurantId: string;
  items: CartItem[];
}

function getCartFromStorage(): CartData | null {
  try {
    const savedCartData = localStorage.getItem('eatzy-cart');
    if (savedCartData) {
      return JSON.parse(savedCartData);
    }
  } catch {
    console.error('Failed to parse cart data');
  }
  return null;
}

function calculateModifierGroupTotal(group: ModifierGroup, selections: { [groupId: string]: string[] }): number {
  const selectedOptionIds = selections[group.id] || [];
  if (selectedOptionIds.length === 0) return 0;

  const selectedPrices: number[] = [];
  let nestedTotal = 0;

  for (const optionId of selectedOptionIds) {
    const option = group.options.find(o => o.id === optionId);
    if (option) {
      selectedPrices.push(option.price || 0);
      if (option.nestedModifiers && option.nestedModifiers.length > 0) {
        for (const nestedGroup of option.nestedModifiers) {
          nestedTotal += calculateModifierGroupTotal(nestedGroup, selections);
        }
      }
    }
  }

  let groupTotal = 0;
  if (group.numFree && group.numFree > 0) {
    const sorted = [...selectedPrices].sort((a, b) => a - b);
    for (let i = 0; i < sorted.length; i++) {
      if (i >= group.numFree) {
        groupTotal += sorted[i];
      }
    }
  } else {
    groupTotal = selectedPrices.reduce((sum, p) => sum + p, 0);
  }

  return groupTotal + nestedTotal;
}

function calculateItemTotal(item: CartItem): number {
  const rawPrice = item.item.price;
  const basePrice = typeof rawPrice === 'number' ? rawPrice : parseFloat(rawPrice || '0');
  
  let modifierTotal = 0;
  if (item.item.modifiers && item.selections) {
    for (const group of item.item.modifiers) {
      modifierTotal += calculateModifierGroupTotal(group, item.selections);
    }
  }
  
  return (basePrice + modifierTotal) * item.quantity;
}

function getEffectiveModifierPrices(group: ModifierGroup, selections: { [groupId: string]: string[] }): Map<string, number> {
  const priceMap = new Map<string, number>();
  const selectedOptionIds = selections[group.id] || [];
  if (selectedOptionIds.length === 0) return priceMap;

  const optionsWithPrices: { id: string; price: number }[] = [];
  for (const optionId of selectedOptionIds) {
    const option = group.options.find(o => o.id === optionId);
    if (option) {
      optionsWithPrices.push({ id: optionId, price: option.price || 0 });
    }
  }

  if (group.numFree && group.numFree > 0) {
    const sorted = [...optionsWithPrices].sort((a, b) => a.price - b.price);
    for (let i = 0; i < sorted.length; i++) {
      priceMap.set(sorted[i].id, i < group.numFree ? 0 : sorted[i].price);
    }
  } else {
    for (const opt of optionsWithPrices) {
      priceMap.set(opt.id, opt.price);
    }
  }

  return priceMap;
}

function transformCartItemsForCheckout(cartItems: CartItem[]): CheckoutCartItem[] {
  return cartItems.map(cartItem => {
    const rawPrice = cartItem.item.price;
    const basePrice = typeof rawPrice === 'number' ? rawPrice : parseFloat(rawPrice || '0');
    
    const modifiers: CheckoutCartItem['modifiers'] = [];
    
    const extractModifiers = (
      groups: ModifierGroup[] | undefined, 
      selections: { [groupId: string]: string[] },
      parentAnswerId?: string
    ) => {
      if (!groups) return;
      
      for (const group of groups) {
        const effectivePrices = getEffectiveModifierPrices(group, selections);
        const selectedOptionIds = selections[group.id] || [];
        for (const optionId of selectedOptionIds) {
          const option = group.options.find(o => o.id === optionId);
          if (option) {
            modifiers.push({
              questionId: group.id,
              answerId: optionId,
              answerText: option.name,
              priceAdjustment: effectivePrices.get(optionId) ?? (option.price || 0),
              parentAnswerId: parentAnswerId,
            });
            
            if (option.nestedModifiers && option.nestedModifiers.length > 0) {
              extractModifiers(option.nestedModifiers, selections, optionId);
            }
          }
        }
      }
    };
    
    if (cartItem.item.modifiers && cartItem.selections) {
      extractModifiers(cartItem.item.modifiers, cartItem.selections);
    }
    
    return {
      itemId: cartItem.item.id,
      name: cartItem.item.name,
      quantity: cartItem.quantity,
      price: basePrice,
      modifiers: modifiers.length > 0 ? modifiers : undefined,
    };
  });
}

function getItemDescription(item: CartItem): string {
  const parts: string[] = [];
  
  // Recursive function to extract option names including nested ones
  const extractOptionNames = (groups: ModifierGroup[] | undefined, selections: { [groupId: string]: string[] }) => {
    if (!groups) return;
    
    for (const group of groups) {
      const selectedOptionIds = selections[group.id] || [];
      for (const optionId of selectedOptionIds) {
        const option = group.options.find(o => o.id === optionId);
        if (option) {
          parts.push(option.name);
          // Recursively extract nested option names
          if (option.nestedModifiers && option.nestedModifiers.length > 0) {
            extractOptionNames(option.nestedModifiers, selections);
          }
        }
      }
    }
  };
  
  if (item.item.modifiers && item.selections) {
    extractOptionNames(item.item.modifiers, item.selections);
  }
  
  return parts.join(', ');
}

export default function Checkout() {
  const [, navigate] = useLocation();
  const [cartData, setCartData] = useState<CartData | null>(null);
  const [orderConfig, setOrderConfig] = useState<OrderConfig | null>(null);
  const [deliveryOption, setDeliveryOption] = useState('hand-it');
  const [promoCode, setPromoCode] = useState('');
  const [giftCertificate, setGiftCertificate] = useState('');
  const [giftCertificateApplied, setGiftCertificateApplied] = useState(false);
  const [appliedGiftCertificateAmount, setAppliedGiftCertificateAmount] = useState(0);
  const [deliveryNotes, setDeliveryNotes] = useState('');
  
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showApartmentModal, setShowApartmentModal] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [showDropoffModal, setShowDropoffModal] = useState(false);
  
  // Payment processing state
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [showEmbeddedCheckout, setShowEmbeddedCheckout] = useState(false);
  const [embeddedCheckoutData, setEmbeddedCheckoutData] = useState<{
    clientSecret: string;
    publishableKey: string;
    stripeAccountId: string;
    checkoutId: string;
    clientId: string;
    amount: number;
    paymentIntentId?: string;
  } | null>(null);
  const { toast } = useToast();
  
  // Apartment override: allows user to edit apartment/unit locally for this checkout session
  const [apartmentOverride, setApartmentOverride] = useState<string | null>(null);
  // Temp state for modal inputs
  const [tempApartment, setTempApartment] = useState('');
  const [tempDeliveryInstructions, setTempDeliveryInstructions] = useState('');
  
  // Get user profile data for addresses and phones
  const { addresses, phones, primaryAddress, isLoading: isLoadingProfile, refreshPhones, refreshAddresses } = useUserProfile();
  
  // State for adding a new phone number inline
  const [isAddingPhone, setIsAddingPhone] = useState(false);
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [isSubmittingPhone, setIsSubmittingPhone] = useState(false);
  
  // Selected address state - initialized from order config during render to avoid effect race conditions
  // This is the fix for the address propagation bug: initializing in useState avoids the race where
  // the primary address effect would run before the config effect's state update was applied
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(() => {
    const config = getOrderConfigFromStorage();
    if (config?.addressId) {
      console.log('[Checkout] Initializing address from config:', config.addressId);
      return config.addressId;
    }
    return null;
  });
  const [selectedPhoneId, setSelectedPhoneId] = useState<number | null>(null);
  // Track that we initialized from config to prevent re-overwriting
  const initializedFromConfig = useRef(selectedAddressId !== null);
  // Idempotency guard: prevents confirm-payment from being called more than once per payment
  const isConfirmingPayment = useRef(false);
  
  // Get the selected address object
  const selectedAddress = useMemo(() => {
    if (selectedAddressId) {
      return addresses.find(a => a.id === selectedAddressId) || null;
    }
    return primaryAddress || addresses[0] || null;
  }, [selectedAddressId, addresses, primaryAddress]);
  
  // Get the selected phone object
  const selectedPhone = useMemo(() => {
    if (selectedPhoneId) {
      return phones.find(p => p.id === selectedPhoneId) || null;
    }
    const primaryPhone = phones.find(p => p.isPrimary);
    return primaryPhone || phones[0] || null;
  }, [selectedPhoneId, phones]);

  // Effective apartment: use override if set, otherwise from the saved address
  const effectiveApartment = apartmentOverride !== null ? apartmentOverride : (selectedAddress?.apartment || '');

  // Reset apartment override when the selected address changes
  useEffect(() => {
    setApartmentOverride(null);
  }, [selectedAddressId]);

  useEffect(() => {
    const cart = getCartFromStorage();
    if (!cart || !cart.items || cart.items.length === 0) {
      navigate('/');
      return;
    }
    setCartData(cart);
    
    // Load order config (address is already initialized from config in useState initializer)
    const config = getOrderConfigFromStorage();
    if (config) {
      setOrderConfig(config);
    }
  }, [navigate]);
  
  // Set primary address ONLY if config doesn't specify one
  useEffect(() => {
    // Skip if we initialized from order config
    if (initializedFromConfig.current) {
      return;
    }
    
    // Double-check orderConfig as a safety net (in case config was written after initial render)
    if (orderConfig?.addressId && !selectedAddressId) {
      console.log('[Checkout] Setting address from orderConfig effect:', orderConfig.addressId);
      setSelectedAddressId(orderConfig.addressId);
      initializedFromConfig.current = true;
      return;
    }
    
    // Fall back to primary address when profile loads (only if no config address)
    if (!isLoadingProfile && addresses.length > 0 && !selectedAddressId && !orderConfig?.addressId) {
      const primary = addresses.find(a => a.isPrimary);
      console.log('[Checkout] No config address, using primary:', primary?.id);
      setSelectedAddressId(primary?.id || addresses[0].id);
    }
  }, [isLoadingProfile, addresses, selectedAddressId, orderConfig]);
  
  // Set primary phone when profile loads
  useEffect(() => {
    if (!isLoadingProfile && phones.length > 0 && !selectedPhoneId) {
      const primary = phones.find(p => p.isPrimary);
      setSelectedPhoneId(primary?.id || phones[0].id);
    }
  }, [isLoadingProfile, phones, selectedPhoneId]);
  
  // Derived values from order config
  const serviceType = orderConfig?.serviceType || 'delivery';

  // Fetch restaurant data for timezone and other info
  const { data: restaurant, isLoading: isLoadingRestaurant } = useQuery<{ id: string; name: string; address?: string; city?: string; state?: string; latitude?: string; longitude?: string; eatzyTimezone?: string }>({
    queryKey: ['/api/restaurants', cartData?.restaurantId],
    enabled: !!cartData?.restaurantId,
  });

  // Fetch delays for ASAP quote
  const { data: delaysData } = useOrderDelays(cartData?.restaurantId);
  
  // Memoize delays to prevent infinite loops
  const menuDelays = useMemo(() => {
    if (!delaysData) return undefined;
    return {
      window: 900,
      delay: delaysData.takeoutSeconds,
      route: delaysData.deliverySeconds,
    };
  }, [delaysData?.takeoutSeconds, delaysData?.deliverySeconds]);

  // Auto-refreshing ASAP quote (updates every minute) - use restaurant timezone for display
  const asapQuote = useAsapQuoteRefresh(menuDelays, serviceType, restaurant?.eatzyTimezone);

  // Format delivery time - use ASAP quote for ASAP orders
  const deliveryTime = useMemo(() => {
    if (orderConfig?.timing === 'asap' && asapQuote) {
      return `${asapQuote.label} ${asapQuote.formattedRange}`;
    }
    return formatScheduledTime(orderConfig?.scheduledTime || null, orderConfig?.timing || 'asap', restaurant?.eatzyTimezone);
  }, [orderConfig?.timing, orderConfig?.scheduledTime, asapQuote, restaurant?.eatzyTimezone]);

  // Tip state - only trigger API call on explicit update
  const [tipAmount, setTipAmount] = useState<number>(0);
  const [customTipDisplay, setCustomTipDisplay] = useState<string>(''); // Display value for custom tip input
  const [appliedTip, setAppliedTip] = useState<number>(0);
  const [hasUserSelectedTip, setHasUserSelectedTip] = useState<boolean>(false);
  const [checkoutData, setCheckoutData] = useState<CheckoutResponse | null>(null);
  const [checkoutErrors, setCheckoutErrors] = useState<string[]>([]);
  const [checkoutStale, setCheckoutStale] = useState(false);

  // Fetch gift certificate balance for this restaurant
  const { data: giftCertificateBalance } = useQuery<{ available_balance: number }>({
    queryKey: ['/api/gift-certificates/balance', checkoutData?.client],
    enabled: !!checkoutData?.client,
  });
  
  const availableGiftBalance = giftCertificateBalance?.available_balance || 0;

  // Redeem gift certificate mutation
  const redeemMutation = useMutation({
    mutationFn: async (code: string) => {
      const res = await apiRequest('POST', '/api/gift-certificates/redeem', { code });
      return res.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Gift certificate redeemed!",
        description: `$${data.amount?.toFixed(2) || '0.00'} has been added to your account`,
      });
      setGiftCertificate('');
      // Invalidate balance query to refresh
      queryClient.invalidateQueries({ queryKey: ['/api/gift-certificates/balance', checkoutData?.client] });
      queryClient.invalidateQueries({ queryKey: ['/api/gift-certificates/balances'] });
    },
    onError: (error: Error) => {
      toast({
        title: "Redemption failed",
        description: error.message || "Could not redeem gift certificate",
        variant: "destructive",
      });
    },
  });

  // State for applied coupons and discounts from Eatzy API
  const [appliedCouponCode, setAppliedCouponCode] = useState<string | null>(null);
  const [appliedCouponDiscount, setAppliedCouponDiscount] = useState(0);

  // Adjust checkout mutation - applies coupons, gift certificates, tips, notes to backend
  const adjustCheckoutMutation = useMutation({
    mutationFn: async (adjustData: {
      couponCode?: string;
      giftCertificate?: { useFullAmount: boolean; amount?: number };
      tipFeeId?: number;
      tipAmount?: number;
      notes?: string;
      deliveryOption?: string;
    }) => {
      if (!checkoutData?.client || !checkoutData?.checkout) {
        throw new Error('Checkout session not initialized');
      }
      const res = await apiRequest('POST', `/api/restaurants/${cartData?.restaurantId}/checkout/adjust`, {
        client: checkoutData.client,
        checkout: checkoutData.checkout,
        phoneId: selectedPhoneId,
        ...adjustData,
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to adjust checkout');
      }
      return res.json();
    },
    onSuccess: (data) => {
      // Update checkout data with new totals from API
      setCheckoutData(data);
      
      // Track applied coupons
      if (data.coupons && data.coupons.length > 0) {
        setAppliedCouponCode(data.coupons[0].code);
        setAppliedCouponDiscount(data.summary?.couponDiscount || data.coupons.reduce((sum: number, c: any) => sum + c.discount, 0));
        toast({ description: `Coupon applied! You saved $${data.summary?.couponDiscount?.toFixed(2) || '0.00'}` });
      }
      
      // Track applied gift certificate
      if (data.giftCertificate) {
        setGiftCertificateApplied(true);
        setAppliedGiftCertificateAmount(data.summary?.giftCertificateApplied || data.giftCertificate.amount || 0);
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to apply",
        description: error.message || "Could not apply changes to checkout",
        variant: "destructive",
      });
    },
  });

  // Apply coupon code
  const handleApplyCoupon = () => {
    if (!promoCode.trim()) return;
    adjustCheckoutMutation.mutate({ couponCode: promoCode.trim() });
  };

  // Apply gift certificate balance via Eatzy API
  const handleApplyGiftCertificate = () => {
    const amountToApply = Math.min(availableGiftBalance, total);
    adjustCheckoutMutation.mutate({ 
      giftCertificate: { 
        useFullAmount: amountToApply >= total,
        amount: amountToApply 
      } 
    });
  };

  // Apply delivery notes and option
  const handleApplyDeliveryDetails = async (notes: string, option: string): Promise<boolean> => {
    if (!checkoutData?.checkout) {
      // Just update local state if no checkout exists yet
      setDeliveryNotes(notes);
      setDeliveryOption(option);
      return true;
    }
    
    try {
      await adjustCheckoutMutation.mutateAsync({ 
        notes, 
        deliveryOption: option === 'leave-at-door' ? 'leave_at_door' : 'meet_at_door' 
      });
      // Only update local state after successful API call
      setDeliveryNotes(notes);
      setDeliveryOption(option);
      return true;
    } catch {
      // Error toast is handled by mutation onError
      return false;
    }
  };

  // Checkout mutation for fetching real fees and taxes
  const checkoutMutation = useInitiateCheckout(cartData?.restaurantId);

  // Build a stable key for checkout request to detect meaningful changes
  // Use selectedAddressId (user's actual selection) instead of orderConfig.addressId (from URL)
  const checkoutKey = useMemo(() => {
    if (!orderConfig) return null;
    return JSON.stringify({
      service: orderConfig.serviceType,
      timing: orderConfig.timing,
      scheduledTime: orderConfig.scheduledTime,
      addressId: selectedAddressId,
      phoneId: selectedPhoneId,
      tip: appliedTip,
      restaurantId: cartData?.restaurantId,
    });
  }, [orderConfig, appliedTip, cartData?.restaurantId, selectedPhoneId, selectedAddressId]);

  // Fetch checkout data when config changes
  useEffect(() => {
    if (!orderConfig || !cartData?.restaurantId) return;
    
    // For delivery orders, wait until we have a selected address
    if (orderConfig.serviceType === 'delivery' && !selectedAddressId) return;

    // Mark current data as potentially stale while fetching
    setCheckoutStale(true);

    const checkoutItems = transformCartItemsForCheckout(cartData.items);
    
    // Use scheduledStart/scheduledEnd directly if available (Unix timestamps from cart)
    // Fall back to converting scheduledTime (ISO string) if needed
    // Eatzy uses 15-minute windows (900 seconds), not 1-hour windows
    const scheduledStart = orderConfig.scheduledStart || 
      (orderConfig.scheduledTime ? Math.floor(new Date(orderConfig.scheduledTime).getTime() / 1000) : undefined);
    const scheduledEnd = orderConfig.scheduledEnd || 
      (scheduledStart ? scheduledStart + 900 : undefined); // 15-minute window (Eatzy standard)
    
    const request = {
      service: orderConfig.serviceType,
      timing: orderConfig.timing,
      scheduledTime: scheduledStart, // Keep for backward compatibility
      scheduledStart: scheduledStart,
      scheduledEnd: scheduledEnd,
      addressId: selectedAddressId || undefined,
      phoneId: selectedPhoneId || undefined,
      tip: appliedTip > 0 ? appliedTip : undefined,
      items: checkoutItems,
    };

    checkoutMutation.mutate(request, {
      onSuccess: (data) => {
        setCheckoutStale(false);
        if (data.valid) {
          setCheckoutData(data);
          setCheckoutErrors([]);
        } else {
          setCheckoutData(null);
          setCheckoutErrors(data.errors || ['Checkout validation failed']);
        }
      },
      onError: (error: any) => {
        setCheckoutStale(false);
        setCheckoutData(null);
        // The error message now comes from the backend with user-friendly text
        const errorMessage = error?.message || 'Unable to verify checkout. Please try again.';
        setCheckoutErrors([errorMessage]);
        console.error('Checkout error:', errorMessage, error);
      },
    });
  }, [checkoutKey]);

  // Apply tip selection (updates the applied tip which will be used in final checkout)
  const handleTipSelection = (amount: number) => {
    setTipAmount(amount);
    setAppliedTip(amount);
    setHasUserSelectedTip(true);
    // Always show the selected tip amount in the custom field, formatted to 2 decimal places
    setCustomTipDisplay(amount.toFixed(2));
  };

  const isLoadingCheckout = checkoutMutation.isPending || checkoutStale;


  const { subtotal: localSubtotal, totalItems, itemDetails } = useMemo(() => {
    if (!cartData?.items) {
      return { subtotal: 0, totalItems: 0, itemDetails: [] };
    }
    
    const items = cartData.items.map(item => ({
      name: item.item.name,
      quantity: item.quantity,
      price: calculateItemTotal(item),
      description: getItemDescription(item),
      specialInstructions: item.specialInstructions || '',
    }));
    
    return {
      subtotal: items.reduce((sum, item) => sum + item.price, 0),
      totalItems: cartData.items.reduce((sum, item) => sum + item.quantity, 0),
      itemDetails: items,
    };
  }, [cartData]);

  // Set default tip when checkout data is received (only if user hasn't manually selected)
  useEffect(() => {
    if (!checkoutData || hasUserSelectedTip) return;
    
    // Use API's subtotal or fallback to local calculation
    const baseSubtotal = checkoutData.summary?.subtotal ?? localSubtotal;
    if (baseSubtotal <= 0) return;
    
    // Apply default tip percentage from API
    if (checkoutData.defaultTipPercent !== undefined) {
      const defaultTipAmount = Math.round(baseSubtotal * (checkoutData.defaultTipPercent / 100) * 100) / 100;
      setTipAmount(defaultTipAmount);
      setAppliedTip(defaultTipAmount);
      // Show the default tip amount in the custom field
      setCustomTipDisplay(defaultTipAmount.toFixed(2));
    }
  }, [checkoutData, localSubtotal, hasUserSelectedTip]);

  // Use API data if available, otherwise fallback to local calculation
  const subtotal = checkoutData?.summary?.subtotal ?? localSubtotal;
  const deliveryFee = checkoutData?.summary?.deliveryFee ?? 0.00;
  const serviceFee = checkoutData?.summary?.serviceFee ?? 0.00;
  const smallOrderFee = 0.00; // Not provided by API, keep as hardcoded
  const taxes = checkoutData?.summary?.tax ?? (localSubtotal * 0.089);
  // Use local appliedTip state for display - this reflects user's selection
  const tip = appliedTip;
  // Calculate total using local tip value (API total uses API's tip, not user's selection)
  // Start with API total and adjust for tip difference, or calculate from scratch
  const apiTip = checkoutData?.summary?.tip ?? 0;
  const apiTotal = checkoutData?.summary?.total;
  const total = apiTotal !== undefined 
    ? (apiTotal - apiTip + tip) // Adjust API total by replacing API tip with local tip
    : (localSubtotal + deliveryFee + serviceFee + smallOrderFee + taxes + tip);
  
  const KUDOZ_PER_DOLLAR = 7;
  const kudozToEarn = Math.floor(subtotal * KUDOZ_PER_DOLLAR);

  // Handle Eatzy API payment flow (existing flow - redirects to Stripe hosted page)
  const handleEatzyPaymentFlow = async () => {
    setIsPaymentProcessing(true);

    try {
      let clientId = checkoutData?.client;
      let checkoutId = checkoutData?.checkout;
      
      if (!clientId || !checkoutId) {
        if (!cartData?.restaurantId || !orderConfig || checkoutMutation.isPending) {
          throw new Error('Checkout not initialized. Please try again.');
        }
        console.log('[Checkout] Re-initializing expired checkout session...');
        const checkoutItems = transformCartItemsForCheckout(cartData.items);
        const scheduledStart = orderConfig.scheduledStart || 
          (orderConfig.scheduledTime ? Math.floor(new Date(orderConfig.scheduledTime).getTime() / 1000) : undefined);
        const scheduledEnd = orderConfig.scheduledEnd || 
          (scheduledStart ? scheduledStart + 900 : undefined);
        const reinitResult = await checkoutMutation.mutateAsync({
          service: orderConfig.serviceType,
          timing: orderConfig.timing,
          scheduledTime: scheduledStart,
          scheduledStart,
          scheduledEnd,
          addressId: selectedAddressId || undefined,
          phoneId: selectedPhoneId || undefined,
          tip: appliedTip > 0 ? appliedTip : undefined,
          items: checkoutItems,
        });
        if (!reinitResult.valid) {
          throw new Error(reinitResult.errors?.[0] || 'Checkout validation failed');
        }
        setCheckoutData(reinitResult);
        setCheckoutErrors([]);
        clientId = reinitResult.client;
        checkoutId = reinitResult.checkout;
        if (!clientId || !checkoutId) {
          throw new Error('Checkout not initialized. Please try again.');
        }
      }
      
      // Build success and cancel URLs for Stripe redirect
      const baseUrl = window.location.origin;
      const successUrl = `${baseUrl}/order-success/${checkoutId}/${clientId}`;
      const cancelUrl = `${baseUrl}/checkout`;
      
      console.log('[Checkout] Creating Stripe payment with:', { 
        client: clientId, 
        checkout: checkoutId,
        success_url: successUrl,
        cancel_url: cancelUrl 
      });
      
      const response = await apiRequest('POST', '/api/eatzy/payments/stripe/new', { 
        client: clientId,
        checkout: checkoutId,
        success_url: successUrl,
        cancel_url: cancelUrl
      });
      const stripeData = await response.json();
      
      console.log('[Checkout] Stripe payment response:', stripeData);

      if (stripeData.error) {
        throw new Error(stripeData.error);
      }

      // The redirect URL is the Stripe checkout page (checkout.stripe.com)
      // NOT the success_url which is where Stripe redirects after payment
      // Note: Eatzy API returns this as 'redirection_url' not 'redirect'
      const stripeCheckoutUrl = stripeData.redirection_url;
      
      console.log('[Checkout] Redirecting to Stripe checkout:', stripeCheckoutUrl);
      
      if (stripeCheckoutUrl) {
        // Clear cart before redirecting to Stripe
        localStorage.removeItem('eatzy-cart');
        localStorage.removeItem('eatzy-order-config');
        
        // Redirect to actual Stripe checkout page
        window.location.href = stripeCheckoutUrl;
      } else {
        throw new Error('No Stripe checkout URL received');
      }
    } catch (error: any) {
      console.error('Payment error:', error);
      toast({
        title: 'Payment Failed',
        description: error.message || 'Unable to start payment. Please try again.',
        variant: 'destructive',
      });
      setIsPaymentProcessing(false);
    }
  };

  // Handle embedded payment flow using Eatzy's embedded checkout API
  // Flow: create-payment-intent (via Eatzy Connect platform) → show Stripe Elements form → confirm-payment
  const handleEmbeddedPaymentFlow = async (_publishableKey: string) => {
    setIsPaymentProcessing(true);

    try {
      let clientId = checkoutData?.client;
      let checkoutId = checkoutData?.checkout;

      if (!clientId || !checkoutId) {
        if (!cartData?.restaurantId || !orderConfig || checkoutMutation.isPending) {
          throw new Error('Checkout not initialized. Please try again.');
        }
        console.log('[Checkout] Re-initializing expired checkout session for embedded flow...');
        const checkoutItems = transformCartItemsForCheckout(cartData.items);
        const scheduledStart = orderConfig.scheduledStart ||
          (orderConfig.scheduledTime ? Math.floor(new Date(orderConfig.scheduledTime).getTime() / 1000) : undefined);
        const scheduledEnd = orderConfig.scheduledEnd ||
          (scheduledStart ? scheduledStart + 900 : undefined);
        const reinitResult = await checkoutMutation.mutateAsync({
          service: orderConfig.serviceType,
          timing: orderConfig.timing,
          scheduledTime: scheduledStart,
          scheduledStart,
          scheduledEnd,
          addressId: selectedAddressId || undefined,
          phoneId: selectedPhoneId || undefined,
          tip: appliedTip > 0 ? appliedTip : undefined,
          items: checkoutItems,
        });
        if (!reinitResult.valid) {
          throw new Error(reinitResult.errors?.[0] || 'Checkout validation failed');
        }
        setCheckoutData(reinitResult);
        setCheckoutErrors([]);
        clientId = reinitResult.client;
        checkoutId = reinitResult.checkout;
        if (!clientId || !checkoutId) {
          throw new Error('Checkout not initialized. Please try again.');
        }
      }

      // Step 1: Create PaymentIntent via Eatzy's Stripe Connect platform
      console.log('[Checkout] Step 1: Creating PaymentIntent via Eatzy API...');
      let piResponse = await apiRequest('POST', '/api/stripe/create-payment-intent', {
        checkoutId,
        clientId,
      });
      let piData = await piResponse.json();
      console.log('[Checkout] PaymentIntent response:', piData);

      if (!piData.clientSecret || !piData.publishableKey) {
        throw new Error(piData.error || 'Failed to create payment. Please try again.');
      }

      // Step 2: Show the embedded payment form using Eatzy's Stripe keys
      // piData.amount is in cents from Eatzy/Stripe, convert to dollars for display
      const displayAmountDollars = piData.amount ? piData.amount / 100 : total;
      setEmbeddedCheckoutData({
        clientSecret: piData.clientSecret,
        publishableKey: piData.publishableKey,
        stripeAccountId: piData.stripeAccountId,
        checkoutId: checkoutId!,
        clientId: clientId!,
        amount: displayAmountDollars,
        paymentIntentId: piData.paymentIntentId,
      });
      setShowEmbeddedCheckout(true);
      setIsPaymentProcessing(false);
    } catch (error: any) {
      console.error('Embedded payment error:', error);
      toast({
        title: 'Payment Setup Failed',
        description: error.message || 'Unable to start payment. Please try again.',
        variant: 'destructive',
      });
      setIsPaymentProcessing(false);
    }
  };

  // Handle successful payment from embedded checkout - confirms with Eatzy API
  const handleEmbeddedPaymentSuccess = async (paymentIntentId: string) => {
    if (!embeddedCheckoutData) return;
    if (isConfirmingPayment.current) return;
    isConfirmingPayment.current = true;

    try {
      console.log('[Checkout] Step 4: Confirming payment with Eatzy API...', paymentIntentId);

      const confirmResponse = await apiRequest('POST', '/api/stripe/confirm-payment', {
        paymentIntentId,
        checkoutId: embeddedCheckoutData.checkoutId,
        clientId: embeddedCheckoutData.clientId,
      });
      const confirmData = await confirmResponse.json();
      console.log('[Checkout] Payment confirmation result:', confirmData);

      if (confirmData.error || confirmData.success === false) {
        throw new Error(confirmData.error || 'Order confirmation failed after payment. Please contact support.');
      }

      localStorage.removeItem('eatzy-cart');
      localStorage.removeItem('eatzy-order-config');
      setShowEmbeddedCheckout(false);
      setEmbeddedCheckoutData(null);

      navigate(`/order-success/${embeddedCheckoutData.checkoutId}/${embeddedCheckoutData.clientId}`);
    } catch (error: any) {
      console.error('[Checkout] Payment confirmation error (payment succeeded, navigating to success):', error?.message || error);
      localStorage.removeItem('eatzy-cart');
      localStorage.removeItem('eatzy-order-config');
      setShowEmbeddedCheckout(false);
      navigate(`/order-success/${embeddedCheckoutData.checkoutId}/${embeddedCheckoutData.clientId}`);
    }
  };

  // Handle place order - checks for embedded checkout first, falls back to Eatzy flow
  const handlePlaceOrder = async () => {
    if (checkoutErrors.length > 0 || isLoadingCheckout || isPaymentProcessing) {
      return;
    }

    if (serviceType === 'delivery' && !selectedAddress) {
      toast({
        title: 'Address Required',
        description: 'Please select a delivery address before placing your order.',
        variant: 'destructive',
      });
      return;
    }

    if (!selectedPhone) {
      toast({
        title: 'Phone Required',
        description: 'Please add a phone number before placing your order.',
        variant: 'destructive',
      });
      return;
    }

    // Check if embedded Stripe checkout is enabled
    let useEmbedded = false;
    let stripeConfig: { publishableKey: string; enabled: boolean } | null = null;
    try {
      const configResponse = await fetch('/api/stripe/config');
      stripeConfig = await configResponse.json();
      useEmbedded = !!stripeConfig?.enabled;
    } catch (error) {
      console.log('Embedded checkout not available, using Eatzy flow');
    }

    if (useEmbedded && stripeConfig) {
      await handleEmbeddedPaymentFlow(stripeConfig.publishableKey);
    } else {
      await handleEatzyPaymentFlow();
    }
  };

  if (!cartData) {
    return (
      <div className="flex flex-col min-h-screen bg-background items-center justify-center p-4">
        <ShoppingCart className="w-16 h-16 text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold mb-2">Your cart is empty</h2>
        <p className="text-muted-foreground mb-4">Add items to your cart to checkout</p>
        <Button onClick={() => navigate('/')} data-testid="button-browse-restaurants">
          Browse Restaurants
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-background pb-20">
      <div className="sticky top-0 z-10 bg-background border-b">
        <div className="p-4 flex items-center justify-between max-w-5xl mx-auto">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => window.history.back()}
            data-testid="button-back"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="text-center flex-1">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">Checkout</div>
            {isLoadingRestaurant ? (
              <Skeleton className="h-5 w-32 mx-auto" />
            ) : (
              <div className="font-semibold">{restaurant?.name || 'Restaurant'}</div>
            )}
          </div>
          <div className="w-10" />
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-4 lg:p-6 grid grid-cols-1 lg:grid-cols-2 lg:gap-x-8 gap-y-6 lg:items-start pb-28">
        {/* Left column: service info, cart, promo, gift certificate, tip */}
        <div className="space-y-6">
        <div className="bg-primary/10 rounded-lg p-4 space-y-2">
          <div className="flex items-center gap-2">
            <Badge variant="default" className="font-semibold" data-testid="badge-service-type">
              {serviceType === 'delivery' ? 'DELIVERY' : 'TAKEOUT'}
            </Badge>
            <Clock className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium" data-testid="text-delivery-time">{deliveryTime}</span>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold">Cart Summary • {totalItems} item{totalItems !== 1 ? 's' : ''}</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                const modifyParams = new URLSearchParams();
                modifyParams.set('modifying', '1');
                if (serviceType) modifyParams.set('serviceType', serviceType);
                const queryStr = `?${modifyParams.toString()}`;
                const dest = restaurant?.id
                  ? `/restaurant/${restaurant.id}${queryStr}`
                  : cartData?.restaurantId
                    ? `/r/${cartData.restaurantId}${queryStr}`
                    : '/';
                navigate(dest);
              }}
              data-testid="button-modify-order"
            >
              <UtensilsCrossed className="w-3.5 h-3.5 mr-1.5" />
              Modify Order
            </Button>
          </div>
          <div className="space-y-2 text-sm">
            {itemDetails.map((item, index) => (
              <div key={index} className="flex justify-between">
                <div className="flex-1">
                  <span className="text-foreground">{item.quantity} × {item.name}</span>
                  {item.description && (
                    <div className="text-xs text-muted-foreground">{item.description}</div>
                  )}
                  {item.specialInstructions && (
                    <div className="text-xs text-muted-foreground italic">"{item.specialInstructions}"</div>
                  )}
                </div>
                <span className="text-muted-foreground">${item.price.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="promo-code" className="font-semibold">Promo code</Label>
          {appliedCouponCode ? (
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-900/40 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                    Code Applied: <span className="font-bold">{appliedCouponCode}</span>
                  </p>
                  <p className="text-lg font-bold text-blue-600">
                    -${appliedCouponDiscount.toFixed(2)} discount
                  </p>
                </div>
                <Badge className="bg-blue-100 text-blue-700 border-blue-300">
                  <Check className="w-3 h-3 mr-1" />
                  Applied
                </Badge>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <Input
                id="promo-code"
                placeholder="Enter promo code"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                className="flex-1 uppercase"
                data-testid="input-promo-code"
              />
              {promoCode && (
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                  onClick={handleApplyCoupon}
                  disabled={adjustCheckoutMutation.isPending || !checkoutData?.checkout}
                  data-testid="button-apply-promo"
                >
                  {adjustCheckoutMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Apply'
                  )}
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="space-y-3">
          <Label className="font-semibold flex items-center gap-2">
            <Gift className="w-4 h-4 text-orange-500" />
            Gift Certificate
          </Label>
          
          {/* Show available balance if user has one for this restaurant */}
          {availableGiftBalance > 0 && (
            <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900/40 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-800 dark:text-green-300">
                    Available Balance
                  </p>
                  <p className="text-lg font-bold text-green-600">
                    ${availableGiftBalance.toFixed(2)}
                  </p>
                </div>
                {!giftCertificateApplied ? (
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-green-600 text-green-600 hover:bg-green-50"
                    onClick={handleApplyGiftCertificate}
                    disabled={adjustCheckoutMutation.isPending || !checkoutData?.checkout}
                    data-testid="button-apply-gift-balance"
                  >
                    {adjustCheckoutMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-1" />
                    ) : null}
                    Apply to Order
                  </Button>
                ) : (
                  <Badge className="bg-green-100 text-green-700 border-green-300">
                    <Check className="w-3 h-3 mr-1" />
                    Applied
                  </Badge>
                )}
              </div>
            </div>
          )}
          
          {/* Input for redeeming a gift certificate code */}
          <div className="flex gap-2">
            <Input
              id="gift-certificate"
              placeholder="Enter gift certificate code"
              value={giftCertificate}
              onChange={(e) => setGiftCertificate(e.target.value.toUpperCase())}
              className="flex-1 uppercase"
              data-testid="input-gift-certificate"
            />
            {giftCertificate && (
              <Button 
                variant="outline" 
                size="sm" 
                className="shrink-0"
                onClick={() => redeemMutation.mutate(giftCertificate)}
                disabled={redeemMutation.isPending}
              >
                {redeemMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Redeem'
                )}
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Enter a gift certificate code to add funds to your account
          </p>
        </div>

        <div className="space-y-3">
          <Label className="font-semibold">Add a tip</Label>
          <div className="flex gap-2 flex-wrap">
            {(() => {
              // API-provided tip options (from the gratuity fee's adjustable_percentages, or
              // any other fee that exposes them). The fallback is only reached when the Eatzy
              // API returns no adjustable_percentages at all for this order mode.
              const tipPercentages = checkoutData?.tipOptions ?? [0, 15, 18, 20];
              const defaultPercent = checkoutData?.defaultTipPercent ?? 15;
              
              // Generate tip option buttons from percentages
              return tipPercentages.map((percent) => {
                const value = percent === 0 ? 0 : Math.round(localSubtotal * (percent / 100) * 100) / 100;
                const label = percent === 0 ? 'No Tip' : `${percent}%`;
                const isDefault = percent === defaultPercent;
                
                return (
                  <Button
                    key={label}
                    variant={appliedTip === value ? 'default' : 'outline'}
                    onClick={() => handleTipSelection(value)}
                    data-testid={`button-tip-${label.toLowerCase().replace(/\s/g, '-').replace('%', '')}`}
                    className={isDefault && appliedTip !== value ? 'ring-1 ring-primary/30' : ''}
                  >
                    {label}
                    {value > 0 && <span className="text-xs ml-1">(${value.toFixed(2)})</span>}
                  </Button>
                );
              });
            })()}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Other amount:</span>
            <div className="flex items-center gap-2">
              <div className="relative w-24">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  id="custom-tip"
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  className="pl-6 text-right"
                  value={customTipDisplay}
                  onChange={(e) => {
                    // Allow only valid currency input (digits and at most one decimal point)
                    const input = e.target.value;
                    if (input === '' || /^\d*\.?\d{0,2}$/.test(input)) {
                      setCustomTipDisplay(input);
                      const val = parseFloat(input);
                      setTipAmount(isNaN(val) ? 0 : Math.max(0, val));
                    }
                  }}
                  onBlur={() => {
                    // Format to 2 decimal places on blur
                    if (customTipDisplay && customTipDisplay !== '') {
                      const val = parseFloat(customTipDisplay);
                      if (!isNaN(val) && val >= 0) {
                        setCustomTipDisplay(val.toFixed(2));
                        setTipAmount(Math.round(val * 100) / 100);
                      } else {
                        setCustomTipDisplay('');
                        setTipAmount(0);
                      }
                    }
                  }}
                  data-testid="input-custom-tip"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleTipSelection(tipAmount)}
                disabled={tipAmount === appliedTip}
                data-testid="button-apply-tip"
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
        </div>{/* end left column */}

        {/* Right column: delivery details and order summary */}
        <div className="space-y-6">
        {serviceType === 'delivery' ? (
          <div className="space-y-4">
            <h3 className="font-semibold">Delivery Details</h3>

            {/* Static Map showing delivery location */}
            {/* Use selectedAddress coordinates first, fall back to orderConfig stored coordinates */}
            {(() => {
              const mapLat = selectedAddress?.latitude ?? orderConfig?.addressLatitude;
              const mapLng = selectedAddress?.longitude ?? orderConfig?.addressLongitude;
              if (mapLat == null || mapLng == null) return null;
              return (
                <div className="relative overflow-hidden rounded-lg border bg-muted">
                  <AuthenticatedMap
                    lat={mapLat}
                    lng={mapLng}
                    zoom={15}
                    width={600}
                    height={128}
                    className="w-full rounded-lg"
                    alt="Delivery location map"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                    <div className="text-white text-sm font-medium truncate">
                      {selectedAddress?.streetAddress || 'Delivery Location'}
                    </div>
                    <div className="text-white/80 text-xs">
                      {selectedAddress ? `${selectedAddress.city}, ${selectedAddress.state} ${selectedAddress.zipCode}` : 'Loading address...'}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Address Selection */}
            <div
              className="flex items-center justify-between p-3 border rounded-lg hover-elevate cursor-pointer"
              onClick={() => setShowAddressModal(true)}
              data-testid="button-address"
            >
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-muted-foreground" />
                <div>
                  {isLoadingProfile ? (
                    <Skeleton className="h-4 w-40" />
                  ) : selectedAddress ? (
                    <>
                      <div className="font-medium">{selectedAddress.streetAddress}</div>
                      <div className="text-xs text-muted-foreground">
                        {selectedAddress.city}, {selectedAddress.state} {selectedAddress.zipCode}
                      </div>
                    </>
                  ) : (
                    <div className="text-muted-foreground">Add delivery address</div>
                  )}
                </div>
              </div>
              <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>

            {/* Apartment/Unit */}
            <div
              className="flex items-center justify-between p-3 border rounded-lg hover-elevate cursor-pointer"
              onClick={() => {
                setTempApartment(effectiveApartment);
                setShowApartmentModal(true);
              }}
              data-testid="button-apartment"
            >
              <div className="flex items-center gap-3">
                <Home className="w-5 h-5 text-muted-foreground" />
                <div>
                  <div className="font-medium">Apartment</div>
                  <div className="text-xs text-muted-foreground">
                    {effectiveApartment || 'Add apt/unit number'}
                  </div>
                </div>
              </div>
              <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>

            {/* Dropoff Options */}
            <div
              className="flex items-center justify-between p-3 border rounded-lg hover-elevate cursor-pointer"
              onClick={() => {
                setTempDeliveryInstructions(deliveryNotes);
                setShowDropoffModal(true);
              }}
              data-testid="button-dropoff"
            >
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-muted-foreground" />
                <div>
                  <div className="font-medium">
                    {deliveryOption === 'hand-it' ? 'Hand it to me' : 'Leave at door'}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {deliveryNotes || (deliveryOption === 'hand-it' 
                      ? 'e.g. ring the bell after dropoff' 
                      : 'Leave order at the door')}
                  </div>
                </div>
              </div>
              <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>

            {/* Phone Number */}
            <div
              className="flex items-center justify-between p-3 border rounded-lg hover-elevate cursor-pointer"
              onClick={() => setShowPhoneModal(true)}
              data-testid="button-phone"
            >
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-muted-foreground" />
                {isLoadingProfile ? (
                  <Skeleton className="h-4 w-32" />
                ) : (
                  <div className="font-medium">
                    {selectedPhone ? `${selectedPhone.countryCode} ${selectedPhone.phoneNumber}` : 'Add phone number'}
                  </div>
                )}
              </div>
              <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <h3 className="font-semibold">Pickup Details</h3>
            
            {/* Static Map showing restaurant location */}
            {restaurant?.latitude && restaurant?.longitude ? (
              <div className="relative overflow-hidden rounded-lg border bg-muted">
                <AuthenticatedMap
                  lat={parseFloat(restaurant.latitude)}
                  lng={parseFloat(restaurant.longitude)}
                  zoom={15}
                  width={600}
                  height={128}
                  className="w-full rounded-lg"
                  alt="Restaurant location map"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                  <div className="text-white text-sm font-medium truncate">
                    {restaurant?.name || 'Restaurant'}
                  </div>
                  <div className="text-white/80 text-xs">
                    {restaurant?.address || ''}{restaurant?.city && `, ${restaurant.city}`} {restaurant?.state || ''}
                  </div>
                </div>
              </div>
            ) : (
              <div className="w-full h-32 bg-muted rounded-lg flex items-center justify-center relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-green-500/20 to-emerald-500/20" />
                <Package className="w-8 h-8 text-primary z-10" />
              </div>
            )}

            {/* Restaurant Address */}
            <div className="flex items-center gap-3 p-3 border rounded-lg">
              <MapPin className="w-5 h-5 text-muted-foreground" />
              <div>
                <div className="font-medium">{restaurant?.name || 'Restaurant'}</div>
                <div className="text-xs text-muted-foreground">
                  {restaurant?.address || ''}{restaurant?.city && `, ${restaurant.city}`} {restaurant?.state || ''}
                </div>
              </div>
            </div>

            {/* Phone Number for Pickup Notification */}
            <div
              className="flex items-center justify-between p-3 border rounded-lg hover-elevate cursor-pointer"
              onClick={() => setShowPhoneModal(true)}
              data-testid="button-phone"
            >
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-muted-foreground" />
                {isLoadingProfile ? (
                  <Skeleton className="h-4 w-32" />
                ) : (
                  <div>
                    <div className="font-medium">
                      {selectedPhone ? `${selectedPhone.countryCode} ${selectedPhone.phoneNumber}` : 'Add phone number'}
                    </div>
                    <div className="text-xs text-muted-foreground">We'll text when your order is ready</div>
                  </div>
                )}
              </div>
              <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        )}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold">Summary</h3>
            {isLoadingCheckout && (
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            )}
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Subtotal</span>
              <span>${subtotal.toFixed(2)}</span>
            </div>
            {/* Display delivery fee separately */}
            {(() => {
              // Get delivery fee from fees array or fallback to summary
              const deliveryFeeFromApi = checkoutData?.fees?.find(fee => 
                fee.name?.toLowerCase().includes('delivery')
              );
              const displayDeliveryFee = deliveryFeeFromApi?.amount ?? deliveryFee;
              
              if (displayDeliveryFee > 0) {
                return (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{deliveryFeeFromApi?.name || 'Delivery Fee'}</span>
                    <span>${displayDeliveryFee.toFixed(2)}</span>
                  </div>
                );
              }
              return null;
            })()}
            {/* Consolidated Taxes and Fees line (excludes delivery and tip) */}
            {(() => {
              // Get all fees except delivery and gratuity (tip)
              const nonDeliveryNonTipFees = checkoutData?.fees?.filter(fee => 
                fee.service_fee_type_id !== 2 && // Not gratuity/tip
                !fee.name?.toLowerCase().includes('delivery') && // Not delivery
                fee.amount > 0
              ) || [];
              
              // Calculate total of service fees + small order fee (fallback values)
              const fallbackServiceFees = serviceFee + smallOrderFee;
              
              // Total from API fees or fallback
              const totalFeesFromApi = nonDeliveryNonTipFees.reduce((sum, fee) => sum + fee.amount, 0);
              const totalOtherFees = totalFeesFromApi > 0 ? totalFeesFromApi : fallbackServiceFees;
              
              // Combined taxes and fees
              const taxesAndFeesTotal = taxes + totalOtherFees;
              
              // Build breakdown items for tooltip
              const breakdownItems: { name: string; amount: number }[] = [];
              
              // Add taxes first
              if (taxes > 0) {
                breakdownItems.push({ name: 'Sales Tax', amount: taxes });
              }
              
              // Add individual fees from API or fallbacks
              if (nonDeliveryNonTipFees.length > 0) {
                nonDeliveryNonTipFees.forEach(fee => {
                  breakdownItems.push({ name: fee.name, amount: fee.amount });
                });
              } else {
                if (serviceFee > 0) {
                  breakdownItems.push({ name: 'Service Fee', amount: serviceFee });
                }
                if (smallOrderFee > 0) {
                  breakdownItems.push({ name: 'Small Order Fee', amount: smallOrderFee });
                }
              }
              
              // Only show if there's something to display
              if (taxesAndFeesTotal <= 0) return null;
              
              // Show simple line if only taxes (no other fees)
              if (breakdownItems.length <= 1) {
                return (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Taxes and Fees</span>
                    <span>${taxesAndFeesTotal.toFixed(2)}</span>
                  </div>
                );
              }
              
              // Show with tooltip breakdown for multiple items
              return (
                <div className="flex justify-between">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-muted-foreground flex items-center gap-1 cursor-help">
                        Taxes and Fees
                        <Info className="w-3 h-3 opacity-50" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="text-xs">
                      <div className="space-y-1">
                        {breakdownItems.map((item, idx) => (
                          <div key={idx} className="flex justify-between gap-4">
                            <span>{item.name}</span>
                            <span>${item.amount.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                  <span>${taxesAndFeesTotal.toFixed(2)}</span>
                </div>
              );
            })()}
            {appliedCouponDiscount > 0 && (
              <div className="flex justify-between text-blue-600">
                <span className="flex items-center gap-1">
                  Promo ({appliedCouponCode})
                </span>
                <span>-${appliedCouponDiscount.toFixed(2)}</span>
              </div>
            )}
            {tip > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Tip</span>
                <span>${tip.toFixed(2)}</span>
              </div>
            )}
            {giftCertificateApplied && appliedGiftCertificateAmount > 0 && (
              <div className="flex justify-between text-green-600">
                <span className="flex items-center gap-1">
                  <Gift className="w-3 h-3" />
                  Gift Certificate
                </span>
                <span>-${appliedGiftCertificateAmount.toFixed(2)}</span>
              </div>
            )}
            <Separator />
            <div className="flex justify-between font-bold text-base">
              <span>Total</span>
              <span data-testid="text-total">
                ${Math.max(0, total).toFixed(2)}
              </span>
            </div>
            
            {checkoutErrors.length > 0 && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3 mt-2">
                <div className="flex items-center gap-2 text-destructive text-sm">
                  <AlertCircle className="w-4 h-4" />
                  <span className="font-medium">Unable to verify order</span>
                </div>
                <ul className="mt-1 text-xs text-destructive/80 list-disc pl-5">
                  {checkoutErrors.map((error, idx) => (
                    <li key={idx}>{error}</li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-muted-foreground">
                  Totals shown are estimates. Please try again to proceed with your order.
                </p>
              </div>
            )}
            
            {!checkoutData && !checkoutErrors.length && !isLoadingCheckout && (
              <div className="text-xs text-muted-foreground text-center mt-2">
                Totals are estimates. Final amount may vary.
              </div>
            )}
            
          </div>
          
          <div className="flex items-center justify-between text-sm pt-2">
            <span className="text-muted-foreground">Kudoz earned</span>
            <KudozDisplay amount={kudozToEarn} size="sm" />
          </div>
        </div>
        </div>{/* end right column */}
      </div>{/* end content grid */}

      {/* Sticky bottom payment bar - constrained to max-w-5xl on wide screens */}
      <div className="fixed bottom-0 inset-x-0 z-40 bg-background border-t shadow-sm safe-area-padding-bottom">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-4">
          {checkoutErrors.length > 0 && (
            <div className="flex-1 min-w-0">
              {checkoutErrors.map((err, i) => (
                <p key={i} className="text-xs text-destructive">{err}</p>
              ))}
            </div>
          )}
          {checkoutErrors.length === 0 && (
            <div className="flex-1 min-w-0">
              <div className="text-xs text-muted-foreground">Order total</div>
              <div className="font-bold text-base">${total.toFixed(2)}</div>
            </div>
          )}
          <Button
            size="lg"
            disabled={checkoutErrors.length > 0 || isLoadingCheckout || isPaymentProcessing}
            onClick={handlePlaceOrder}
            data-testid="button-place-order"
          >
            {isPaymentProcessing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing payment...
              </>
            ) : isLoadingCheckout ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Calculating totals...
              </>
            ) : (
              <>
                <CreditCard className="w-4 h-4 mr-2" />
                Pay now • ${total.toFixed(2)}
              </>
            )}
          </Button>
        </div>
      </div>

      <Dialog open={showAddressModal} onOpenChange={setShowAddressModal}>
        <DialogContent className="sm:max-w-md" data-testid="modal-address">
          <DialogHeader>
            <DialogTitle>Select Delivery Address</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-4 max-h-[60svh] overflow-y-auto">
            {isLoadingProfile ? (
              <div className="space-y-3">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
            ) : addresses.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No saved addresses</p>
                <p className="text-sm">Add an address in your Eatzy profile</p>
              </div>
            ) : (
              addresses.map((addr) => (
                <div
                  key={addr.id}
                  className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover-elevate ${
                    selectedAddressId === addr.id ? 'border-primary bg-primary/5' : ''
                  }`}
                  onClick={() => {
                    setSelectedAddressId(addr.id);
                    setShowAddressModal(false);
                  }}
                  data-testid={`address-option-${addr.id}`}
                >
                  <div className={`p-1.5 rounded-full ${selectedAddressId === addr.id ? 'bg-primary' : 'bg-muted'}`}>
                    {addr.label?.toLowerCase().includes('home') ? (
                      <Home className={`w-4 h-4 ${selectedAddressId === addr.id ? 'text-white' : 'text-muted-foreground'}`} />
                    ) : (
                      <MapPin className={`w-4 h-4 ${selectedAddressId === addr.id ? 'text-white' : 'text-muted-foreground'}`} />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium flex items-center gap-2">
                      {addr.label || 'Address'}
                      {addr.isPrimary && (
                        <Badge variant="secondary" className="text-xs">Default</Badge>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">{addr.streetAddress}</div>
                    <div className="text-xs text-muted-foreground">
                      {addr.city}, {addr.state} {addr.zipCode}
                    </div>
                    {addr.apartment && (
                      <div className="text-xs text-muted-foreground">Apt: {addr.apartment}</div>
                    )}
                  </div>
                  {selectedAddressId === addr.id && (
                    <div className="text-primary">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showApartmentModal} onOpenChange={setShowApartmentModal}>
        <DialogContent className="sm:max-w-md" data-testid="modal-apartment">
          <DialogHeader>
            <DialogTitle>Apartment / Unit</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedAddress && (
              <div className="text-sm text-muted-foreground">
                {selectedAddress.streetAddress}, {selectedAddress.city}, {selectedAddress.state} {selectedAddress.zipCode}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="apartment-number">Apt / Suite / Unit</Label>
              <Input
                id="apartment-number"
                value={tempApartment}
                onChange={(e) => setTempApartment(e.target.value)}
                placeholder="e.g., Apt 4B, Suite 200, Unit 12"
                data-testid="input-apartment-number"
              />
            </div>
            <Button
              className="w-full"
              onClick={async () => {
                if (selectedAddressId) {
                  try {
                    const res = await apiRequest('POST', '/api/user/address/update', {
                      addressId: selectedAddressId,
                      apartment: tempApartment,
                    });
                    if (!res.ok) {
                      const err = await res.json().catch(() => ({}));
                      throw new Error(err.error || 'Failed to update address');
                    }
                    toast({ title: 'Apartment updated' });
                    await refreshAddresses();
                    setApartmentOverride(null);
                  } catch (e: any) {
                    toast({ title: 'Could not save apartment', description: e.message, variant: 'destructive' });
                    setApartmentOverride(tempApartment);
                  }
                } else {
                  setApartmentOverride(tempApartment);
                }
                setShowApartmentModal(false);
              }}
              data-testid="button-save-apartment"
            >
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showPhoneModal} onOpenChange={(open) => {
        setShowPhoneModal(open);
        if (!open) {
          setIsAddingPhone(false);
          setNewPhoneNumber('');
        }
      }}>
        <DialogContent className="sm:max-w-md" data-testid="modal-phone">
          <DialogHeader>
            <DialogTitle>{isAddingPhone ? 'Add Phone Number' : 'Phone Number'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-4 max-h-[60svh] overflow-y-auto">
            {isAddingPhone ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-phone-number">Phone Number</Label>
                  <div className="flex gap-2">
                    <div className="flex items-center px-3 border rounded-lg bg-muted text-sm text-muted-foreground">
                      +1
                    </div>
                    <Input
                      id="new-phone-number"
                      type="tel"
                      value={newPhoneNumber}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setNewPhoneNumber(digits);
                      }}
                      placeholder="(555) 123-4567"
                      data-testid="input-new-phone"
                      autoFocus
                    />
                  </div>
                  {newPhoneNumber.length > 0 && newPhoneNumber.length < 10 && (
                    <p className="text-xs text-muted-foreground">Enter a 10-digit phone number</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setIsAddingPhone(false);
                      setNewPhoneNumber('');
                    }}
                    disabled={isSubmittingPhone}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1"
                    disabled={newPhoneNumber.length < 10 || isSubmittingPhone}
                    onClick={async () => {
                      setIsSubmittingPhone(true);
                      try {
                        const res = await apiRequest('POST', '/api/user/phones', {
                          phoneNumber: newPhoneNumber,
                          countryCode: '+1',
                          isPrimary: true,
                        });
                        const data = await res.json();
                        if (!data.phone) {
                          throw new Error(data.error || 'Failed to add phone number');
                        }
                        await refreshPhones();
                        setSelectedPhoneId(data.phone.id);
                        toast({ description: 'Phone number added' });
                        setIsAddingPhone(false);
                        setNewPhoneNumber('');
                        setShowPhoneModal(false);
                      } catch (err: any) {
                        toast({
                          title: 'Could not add phone',
                          description: err.message || 'Please try again',
                          variant: 'destructive',
                        });
                      } finally {
                        setIsSubmittingPhone(false);
                      }
                    }}
                    data-testid="button-save-phone"
                  >
                    {isSubmittingPhone ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Save
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {isLoadingProfile ? (
                  <div className="space-y-3">
                    <Skeleton className="h-14 w-full" />
                    <Skeleton className="h-14 w-full" />
                  </div>
                ) : phones.length === 0 ? (
                  <div className="text-center py-6 text-muted-foreground">
                    <Phone className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No saved phone numbers</p>
                    <p className="text-sm mb-4">A phone number is required for delivery orders</p>
                    <Button onClick={() => setIsAddingPhone(true)} data-testid="button-add-phone-empty">
                      Add Phone Number
                    </Button>
                  </div>
                ) : (
                  <>
                    {phones.map((phone) => (
                      <div
                        key={phone.id}
                        className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover-elevate ${
                          selectedPhoneId === phone.id ? 'border-primary bg-primary/5' : ''
                        }`}
                        onClick={() => {
                          setSelectedPhoneId(phone.id);
                          setShowPhoneModal(false);
                        }}
                        data-testid={`phone-option-${phone.id}`}
                      >
                        <div className={`p-1.5 rounded-full ${selectedPhoneId === phone.id ? 'bg-primary' : 'bg-muted'}`}>
                          <Phone className={`w-4 h-4 ${selectedPhoneId === phone.id ? 'text-white' : 'text-muted-foreground'}`} />
                        </div>
                        <div className="flex-1">
                          <div className="font-medium flex items-center gap-2">
                            {phone.countryCode} {phone.phoneNumber}
                            {phone.isPrimary && (
                              <Badge variant="secondary" className="text-xs">Default</Badge>
                            )}
                          </div>
                        </div>
                        {selectedPhoneId === phone.id && (
                          <div className="text-primary">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => setIsAddingPhone(true)}
                      data-testid="button-add-phone"
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      Add New Phone Number
                    </Button>
                  </>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showDropoffModal} onOpenChange={setShowDropoffModal}>
        <DialogContent className="sm:max-w-md" data-testid="modal-dropoff">
          <DialogHeader>
            <DialogTitle>Dropoff Options</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <RadioGroup value={deliveryOption} onValueChange={setDeliveryOption}>
              <div className="space-y-3">
                <div className="flex items-center space-x-3 p-3 border rounded-lg">
                  <RadioGroupItem value="hand-it" id="hand-it-modal" data-testid="radio-hand-it-modal" />
                  <Label htmlFor="hand-it-modal" className="flex-1 cursor-pointer font-normal">
                    <div className="font-medium">Hand it to me</div>
                    <div className="text-xs text-muted-foreground">Ring the bell after dropoff</div>
                  </Label>
                </div>
                <div className="flex items-center space-x-3 p-3 border rounded-lg">
                  <RadioGroupItem value="leave-at-door" id="leave-at-door" data-testid="radio-leave-at-door" />
                  <Label htmlFor="leave-at-door" className="flex-1 cursor-pointer font-normal">
                    <div className="font-medium">Leave at door</div>
                    <div className="text-xs text-muted-foreground">Leave order at the door</div>
                  </Label>
                </div>
              </div>
            </RadioGroup>
            <div className="space-y-2">
              <Label htmlFor="dropoff-notes" className="text-muted-foreground">Delivery notes (optional)</Label>
              <Input
                id="dropoff-notes"
                value={tempDeliveryInstructions}
                onChange={(e) => setTempDeliveryInstructions(e.target.value)}
                placeholder="e.g., Ring the bell, leave at gate..."
                data-testid="input-dropoff-notes"
              />
            </div>
            <Button
              className="w-full"
              onClick={async () => {
                const success = await handleApplyDeliveryDetails(tempDeliveryInstructions, deliveryOption);
                if (success) {
                  setShowDropoffModal(false);
                }
              }}
              disabled={adjustCheckoutMutation.isPending}
              data-testid="button-save-dropoff"
            >
              {adjustCheckoutMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Embedded Stripe Checkout Dialog */}
      <Dialog open={showEmbeddedCheckout} onOpenChange={(open) => {
        if (!open) {
          setShowEmbeddedCheckout(false);
          setEmbeddedCheckoutData(null);
          isConfirmingPayment.current = false;
        }
      }}>
        <DialogContent className="sm:max-w-lg max-h-[90svh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Complete Payment
            </DialogTitle>
          </DialogHeader>
          {embeddedCheckoutData ? (
            <EmbeddedCheckout
              stripePublishableKey={embeddedCheckoutData.publishableKey}
              clientSecret={embeddedCheckoutData.clientSecret}
              stripeAccountId={embeddedCheckoutData.stripeAccountId}
              amount={embeddedCheckoutData.amount}
              onPaymentSuccess={handleEmbeddedPaymentSuccess}
              onCancel={() => {
                setShowEmbeddedCheckout(false);
                setEmbeddedCheckoutData(null);
                isConfirmingPayment.current = false;
              }}
            />
          ) : (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
              <p className="text-muted-foreground">Setting up secure payment...</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
